export interface RawNode {
  stage: number; // 0 answer, 1 problem, 2 goal, 3 intervention
  label: string;
  full: string;
}

export interface RawLink {
  source: number;
  target: number;
  value: number;
}

export interface LaidOutNode extends RawNode {
  index: number;
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}

export interface LaidOutLink extends RawLink {
  index: number;
  sourceStage: number;
  sx: number;
  sy: number;
  tx: number;
  ty: number;
  width: number;
  path: string;
}

export interface SankeyMargin {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface SankeyOptions {
  width: number;
  height: number;
  margin: SankeyMargin;
  nodeWidth: number;
  nodePadding: number;
  fractions: Record<number, number>;
}

export interface SankeyResult {
  nodes: LaidOutNode[];
  links: LaidOutLink[];
}

const STAGES = [0, 1, 2, 3];

export function computeSankey(
  rawNodes: RawNode[],
  rawLinks: RawLink[],
  opts: SankeyOptions,
): SankeyResult {
  const { width, height, margin, nodeWidth, nodePadding, fractions } = opts;

  const nodes: LaidOutNode[] = rawNodes.map((n, index) => ({
    ...n,
    index,
    x0: 0,
    x1: 0,
    y0: 0,
    y1: 0,
  }));
  const links: LaidOutLink[] = rawLinks.map((l, index) => ({
    ...l,
    index,
    sourceStage: rawNodes[l.source].stage,
    sx: 0,
    sy: 0,
    tx: 0,
    ty: 0,
    width: 0,
    path: "",
  }));

  // throughflow value per node = max(incoming, outgoing), floored at 1
  const inSum = new Array(nodes.length).fill(0);
  const outSum = new Array(nodes.length).fill(0);
  for (const l of links) {
    outSum[l.source] += l.value;
    inSum[l.target] += l.value;
  }
  const value = nodes.map((_, i) => Math.max(inSum[i], outSum[i], 1));


  const innerW = width - margin.right - margin.left;
  const colX0: Record<number, number> = {};
  STAGES.forEach((s) => {
    colX0[s] = margin.left + (fractions[s] ?? 0) * innerW;
  });
  nodes.forEach((n) => {
    n.x0 = colX0[n.stage];
    n.x1 = n.x0 + nodeWidth;
  });

  // vertical scale (px per unit value): pick the largest k that fits every stage
  const plotH = height - margin.top - margin.bottom;
  let k = Infinity;
  STAGES.forEach((s) => {
    const idx = nodes.filter((n) => n.stage === s).map((n) => n.index);
    if (!idx.length) return;
    const sum = idx.reduce((a, i) => a + value[i], 0);
    const avail = plotH - nodePadding * (idx.length - 1);
    if (sum > 0) k = Math.min(k, avail / sum);
  });
  if (!isFinite(k) || k <= 0) k = 1;

  // stack nodes within each stage, centred vertically
  STAGES.forEach((s) => {
    const stageNodes = nodes.filter((n) => n.stage === s);
    const used =
      stageNodes.reduce((a, n) => a + value[n.index] * k, 0) +
      nodePadding * Math.max(0, stageNodes.length - 1);
    let y = margin.top + Math.max(0, (plotH - used) / 2);
    stageNodes.forEach((n) => {
      n.y0 = y;
      n.y1 = y + value[n.index] * k;
      y = n.y1 + nodePadding;
    });
  });

  // assign link endpoints. Order a node's links by the partner's centre so ribbons
  // don't cross unnecessarily, then stack them along the node edge.
  const centre = (i: number) => (nodes[i].y0 + nodes[i].y1) / 2;
  const outOffset = new Array(nodes.length).fill(0);
  const inOffset = new Array(nodes.length).fill(0);

  const bySource = new Map<number, LaidOutLink[]>();
  const byTarget = new Map<number, LaidOutLink[]>();
  links.forEach((l) => {
    (bySource.get(l.source) ?? bySource.set(l.source, []).get(l.source)!).push(l);
    (byTarget.get(l.target) ?? byTarget.set(l.target, []).get(l.target)!).push(l);
  });

  bySource.forEach((ls, src) => {
    ls.sort((a, b) => centre(a.target) - centre(b.target));
    ls.forEach((l) => {
      l.width = l.value * k;
      l.sx = nodes[src].x1;
      l.sy = nodes[src].y0 + outOffset[src] + l.width / 2;
      outOffset[src] += l.width;
    });
  });
  byTarget.forEach((ls, tgt) => {
    ls.sort((a, b) => centre(a.source) - centre(b.source));
    ls.forEach((l) => {
      l.tx = nodes[tgt].x0;
      l.ty = nodes[tgt].y0 + inOffset[tgt] + l.width / 2;
      inOffset[tgt] += l.width;
    });
  });

  // horizontal cubic ribbon (matches d3.sankeyLinkHorizontal)
  links.forEach((l) => {
    const xm = (l.sx + l.tx) / 2;
    l.path = `M${l.sx},${l.sy}C${xm},${l.sy} ${xm},${l.ty} ${l.tx},${l.ty}`;
  });

  return { nodes, links };
}
